"use client"

import { useEffect, useState, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { useLanguage } from "@/lib/i18n"

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger)
}

const phases = [
  {
    number: "01",
    name: "DISCOVER",
    subtitle: "Immersion & Research",
    description: "Understanding the full picture before forming a point of view.",
    services: [
      "Market & Competitive Analysis",
      "Stakeholder Interviews",
      "Consumer Research & Segmentation",
      "Cultural & Trend Insights",
      "Brand Audit & Diagnostics",
    ],
    accent: "◐",
  },
  {
    number: "02",
    name: "DEFINE",
    subtitle: "Strategy & Positioning",
    description: "Synthesizing insights into clear strategic direction.",
    services: [
      "Brand Positioning & Differentiation",
      "Brand Architecture & Portfolio",
      "Purpose, Vision & Values",
      "Value Proposition Development",
      "Brand Narrative & Story",
    ],
    accent: "◢",
  },
  {
    number: "03",
    name: "DESIGN",
    subtitle: "Expression & System",
    description: "Translating strategy into tangible brand assets.",
    services: [
      "Naming & Nomenclature",
      "Visual Identity System",
      "Verbal Identity & Tone of Voice",
      "Messaging Framework",
      "Brand Guidelines",
      "Digital & Website Experience",
    ],
    accent: "◇",
  },
  {
    number: "04",
    name: "DELIVER",
    subtitle: "Activation & Launch",
    description: "From guidelines to market. Making strategy real.",
    services: [
      "Go-to-Market Strategy",
      "Launch Planning & Execution",
      "Campaign Development",
      "Employer Branding",
      "Internal Engagement",
      "Training & Enablement",
    ],
    accent: "◉",
  },
]

export function MethodSection() {
  const { t, tArray, locale } = useLanguage()
  const [hoveredPhase, setHoveredPhase] = useState<number | null>(null)
  const [expandedPhase, setExpandedPhase] = useState<number | null>(null)
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const phasesRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)

  const phaseKeys = ["discover", "define", "design", "deliver"]
  const localizedPhases = phases.map((phase, i) => ({
    ...phase,
    name: t(`method.phases.${phaseKeys[i]}.name`),
    subtitle: t(`method.phases.${phaseKeys[i]}.subtitle`),
    description: t(`method.phases.${phaseKeys[i]}.description`),
    services: tArray(`method.phases.${phaseKeys[i]}.services`),
  }))

  // GSAP ScrollTrigger animations
  useEffect(() => {
    if (!sectionRef.current) return

    const ctx = gsap.context(() => {
      // Header animation - blur reveal
      const headerElements = headerRef.current?.querySelectorAll(".header-item")
      if (headerElements) {
        gsap.fromTo(
          headerElements,
          { opacity: 0, y: 50, filter: "blur(10px)" },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 1,
            stagger: 0.2,
            ease: "power3.out",
            scrollTrigger: {
              trigger: headerRef.current,
              start: "top 80%",
              toggleActions: "play none none reverse",
            },
          }
        )
      }

      // Phases animation - mask reveal from left (faster)
      const phaseCards = phasesRef.current?.querySelectorAll(".phase-card")
      if (phaseCards) {
        gsap.fromTo(
          phaseCards,
          { opacity: 0, x: -40, clipPath: "inset(0 100% 0 0)" },
          {
            opacity: 1,
            x: 0,
            clipPath: "inset(0 0% 0 0)",
            duration: 0.5,
            stagger: 0.08,
            ease: "power3.out",
            scrollTrigger: {
              trigger: phasesRef.current,
              start: "top 75%",
              toggleActions: "play none none reverse",
            },
          }
        )
      }

      // Footer animation
      if (footerRef.current) {
        gsap.fromTo(
          footerRef.current,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power3.out",
            scrollTrigger: {
              trigger: footerRef.current,
              start: "top 90%",
              toggleActions: "play none none reverse",
            },
          }
        )
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={sectionRef}
      id="approach"
      data-section="method"
      className="py-24 md:py-32 px-6 md:px-8 lg:px-12 relative overflow-hidden bg-[var(--color-bg-primary)]"
    >
      <div className="relative max-w-[1400px] mx-auto">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-16">
          <p
            className={`header-item text-2xl md:text-3xl mb-4 text-[var(--color-accent-gold)] opacity-0${locale !== "zh" ? " rotate-[-2deg]" : ""}`}
            style={{ fontFamily: "var(--font-handwritten)" }}
          >
            {t("method.sectionLabel")}
          </p>

          {/* Large quote */}
          <div className="header-item relative max-w-4xl mx-auto opacity-0">
            <blockquote
              className={`text-3xl md:text-4xl lg:text-5xl leading-relaxed text-[var(--color-text-primary)]${locale !== "zh" ? " italic" : " font-light"}`}
              style={{ fontFamily: "var(--font-display)" }}
            >
              {t("method.quote1")}
              <br />
              {t("method.quote2")}
            </blockquote>
          </div>
        </div>

        {/* Journey Progress Indicator - Desktop only - Aligned with cards */}
        <div className="hidden lg:block relative mb-16">
          {/* Grid layout matching cards below */}
          <div className="grid grid-cols-4 gap-8 relative">
            {/* Connecting line across all nodes */}
            <div
              className="absolute top-5 left-[12.5%] right-[12.5%] h-px"
              style={{ backgroundColor: "rgba(201, 162, 39, 0.2)" }}
            />

            {/* Animated progress line */}
            <motion.div
              className="absolute top-5 left-[12.5%] h-px"
              style={{
                backgroundColor: "var(--color-accent-gold)",
                boxShadow: "0 0 12px rgba(201, 162, 39, 0.5)",
              }}
              initial={{ width: "0%" }}
              animate={{
                width: hoveredPhase !== null ? `${((hoveredPhase + 1) / 4) * 75}%` : "0%",
              }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            />

            {/* Phase nodes - each centered in its grid cell */}
            {localizedPhases.map((phase, index) => (
              <motion.div
                key={phase.number}
                className="relative z-10 flex flex-col items-center cursor-pointer"
                onMouseEnter={() => setHoveredPhase(index)}
                onMouseLeave={() => setHoveredPhase(null)}
              >
                {/* Node circle - larger for better visibility */}
                <motion.div
                  className="w-10 h-10 rounded-full border-2 flex items-center justify-center"
                  style={{
                    backgroundColor:
                      hoveredPhase !== null && hoveredPhase >= index
                        ? "var(--color-accent-gold)"
                        : "var(--color-bg-primary)",
                    borderColor: "var(--color-accent-gold)",
                    boxShadow:
                      hoveredPhase === index
                        ? "0 0 24px rgba(201, 162, 39, 0.6)"
                        : "0 0 8px rgba(201, 162, 39, 0.2)",
                  }}
                  animate={{
                    scale: hoveredPhase === index ? 1.15 : 1,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <span
                    className="text-xs font-mono font-bold"
                    style={{
                      color:
                        hoveredPhase !== null && hoveredPhase >= index
                          ? "var(--color-bg-primary)"
                          : "var(--color-accent-gold)",
                    }}
                  >
                    {phase.number}
                  </span>
                </motion.div>

                {/* Phase label */}
                <motion.span
                  className="mt-3 text-[11px] font-mono uppercase tracking-wider"
                  style={{
                    color:
                      hoveredPhase === index
                        ? "var(--color-accent-gold)"
                        : "var(--color-text-muted)",
                  }}
                  animate={{
                    y: hoveredPhase === index ? -2 : 0,
                  }}
                >
                  {phase.name}
                </motion.span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Desktop: 4-column layout with dividers */}
        <div ref={phasesRef} className="hidden lg:grid lg:grid-cols-4 gap-8 relative">
          {/* Vertical dividers between columns */}
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="absolute h-[80%] w-px top-[10%]"
              style={{
                left: `${i * 25}%`,
                backgroundColor: "rgba(201, 162, 39, 0.15)",
              }}
            />
          ))}

          {localizedPhases.map((phase, index) => (
            <motion.div
              key={phase.number}
              className="phase-card relative p-6 transition-all duration-500 rounded-lg cursor-pointer opacity-0 bg-[var(--color-bg-secondary)]/50"
              style={{
                backgroundColor:
                  hoveredPhase === index
                    ? "var(--color-bg-secondary)"
                    : "rgba(255, 255, 255, 0.02)",
              }}
              onMouseEnter={() => setHoveredPhase(index)}
              onMouseLeave={() => setHoveredPhase(null)}
              whileHover={{
                y: -4,
                boxShadow: "0 12px 40px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(201, 162, 39, 0.2)",
              }}
              transition={{ duration: 0.3 }}
            >
              {/* Enhanced layered decorative number */}
              <div
                className="relative text-6xl md:text-7xl font-bold mb-4"
                style={{ lineHeight: 1 }}
              >
                {/* Background blur layer */}
                <div
                  className="absolute inset-0"
                  style={{
                    fontFamily: "var(--font-display)",
                    color: "var(--color-accent-gold)",
                    opacity: 0.1,
                    filter: "blur(8px)",
                    transform: "translate(-4px, -4px)",
                  }}
                >
                  {phase.number}
                </div>
                {/* Main gradient number */}
                <motion.div
                  style={{
                    fontFamily: "var(--font-display)",
                    background: `linear-gradient(180deg, rgba(201,162,39,0.6) 0%, rgba(201,162,39,0.25) 50%, rgba(201,162,39,0.05) 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                  animate={{
                    opacity: hoveredPhase === index ? 1 : 0.9,
                  }}
                >
                  {phase.number}
                </motion.div>
              </div>

              {/* Phase name */}
              <h3 className="text-xl md:text-2xl font-semibold mb-3 tracking-wider text-[var(--color-text-primary)]">
                {phase.name}
              </h3>

              {/* Accent line */}
              <div className="w-12 h-0.5 mb-3 bg-[var(--color-accent-teal)]" />

              {/* Subtitle */}
              <p
                className={`text-xl md:text-2xl mb-4 text-[var(--color-accent-teal)]${locale !== "zh" ? " italic" : " font-light"}`}
                style={{ fontFamily: "var(--font-display)" }}
              >
                {phase.subtitle}
              </p>

              {/* Description */}
              <p className="text-base mb-6 leading-relaxed text-[var(--color-text-secondary)]">
                {phase.description}
              </p>

              {/* Divider */}
              <div className="w-full h-px mb-6 bg-[var(--color-bg-tertiary)]" />

              {/* Services list with numbered indicators */}
              <ul className="space-y-3">
                {phase.services.map((service, serviceIndex) => (
                  <motion.li
                    key={service}
                    className="text-base leading-relaxed flex items-start gap-3 transition-colors duration-300 group/service text-[var(--color-text-primary)]"
                    whileHover={{ x: 4 }}
                  >
                    <span
                      className="text-[10px] font-mono w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-300"
                      style={{
                        backgroundColor:
                          hoveredPhase === index
                            ? "rgba(201, 162, 39, 0.25)"
                            : "rgba(201, 162, 39, 0.1)",
                        color: "var(--color-accent-gold)",
                        border: "1px solid rgba(201, 162, 39, 0.5)",
                      }}
                    >
                      {String(serviceIndex + 1).padStart(2, "0")}
                    </span>
                    <span className="relative">
                      {service}
                      <span className="absolute bottom-0 left-0 w-0 h-px bg-[var(--color-accent-gold)] transition-all duration-300 group-hover/service:w-full" />
                    </span>
                  </motion.li>
                ))}
              </ul>

              {/* Enhanced geometric accent with animation */}
              <motion.div
                className="absolute bottom-4 right-4 text-4xl"
                style={{ color: "rgba(42, 157, 143, 0.3)" }}
                animate={{
                  opacity: hoveredPhase === index ? 0.6 : 0.3,
                  scale: hoveredPhase === index ? 1.2 : 1,
                  rotate: hoveredPhase === index ? 15 : 0,
                }}
                transition={{ duration: 0.4 }}
              >
                {phase.accent}
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Tablet: 2x2 grid */}
        <div className="hidden md:grid lg:hidden md:grid-cols-2 gap-6">
          {localizedPhases.map((phase, index) => (
            <motion.div
              key={phase.number}
              className="relative p-6 bg-[var(--color-bg-secondary)] border border-[var(--color-bg-tertiary)] rounded-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Enhanced layered number for tablet */}
              <div className="relative text-5xl font-bold mb-3" style={{ lineHeight: 1 }}>
                <div
                  className="absolute inset-0"
                  style={{
                    fontFamily: "var(--font-display)",
                    color: "var(--color-accent-gold)",
                    opacity: 0.05,
                    filter: "blur(6px)",
                    transform: "translate(-3px, -3px)",
                  }}
                >
                  {phase.number}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-display)",
                    background: `linear-gradient(180deg, rgba(201,162,39,0.4) 0%, rgba(201,162,39,0.15) 50%, transparent 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {phase.number}
                </div>
              </div>

              <h3 className="text-xl font-semibold mb-2 tracking-wider text-[var(--color-text-primary)]">
                {phase.name}
              </h3>

              <div className="w-12 h-0.5 mb-2 bg-[var(--color-accent-teal)]" />

              <p
                className={`text-xl mb-3 text-[var(--color-accent-teal)]${locale !== "zh" ? " italic" : " font-light"}`}
                style={{ fontFamily: "var(--font-display)" }}
              >
                {phase.subtitle}
              </p>

              <p className="text-base mb-4 leading-relaxed text-[var(--color-text-secondary)]">
                {phase.description}
              </p>

              <div className="w-full h-px mb-4 bg-[var(--color-bg-tertiary)]" />

              <ul className="space-y-2">
                {phase.services.map((service, serviceIndex) => (
                  <li
                    key={service}
                    className="text-base leading-relaxed flex items-start gap-3 text-[var(--color-text-primary)]"
                  >
                    <span
                      className="text-[10px] font-mono w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{
                        color: "var(--color-accent-gold)",
                        border: "1px solid rgba(201, 162, 39, 0.4)",
                      }}
                    >
                      {String(serviceIndex + 1).padStart(2, "0")}
                    </span>
                    <span>{service}</span>
                  </li>
                ))}
              </ul>

              <motion.div
                className="absolute bottom-4 right-4 text-3xl"
                style={{ color: "rgba(42, 157, 143, 0.3)" }}
                whileHover={{ scale: 1.2, rotate: 15 }}
              >
                {phase.accent}
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Mobile: Accordion style */}
        <div className="md:hidden space-y-4">
          {localizedPhases.map((phase, index) => {
            const isExpanded = expandedPhase === index

            return (
              <motion.div
                key={phase.number}
                className="border border-[var(--color-bg-tertiary)] rounded-lg overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Accordion Header */}
                <button
                  className="w-full p-4 flex items-center justify-between bg-[var(--color-bg-secondary)]"
                  onClick={() => setExpandedPhase(isExpanded ? null : index)}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="text-3xl font-bold"
                      style={{
                        fontFamily: "var(--font-display)",
                        color: "rgba(201, 162, 39, 0.4)",
                      }}
                    >
                      {phase.number}
                    </span>
                    <span className="text-base font-semibold tracking-wider text-[var(--color-text-primary)]">
                      {phase.name}
                    </span>
                  </div>
                  <span
                    className="text-xl transition-transform duration-300 text-[var(--color-accent-teal)]"
                    style={{ transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)" }}
                  >
                    +
                  </span>
                </button>

                {/* Accordion Content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 bg-[var(--color-bg-primary)] border-t border-[var(--color-bg-tertiary)]">
                        <p
                          className={`text-lg mb-3 text-[var(--color-accent-teal)]${locale !== "zh" ? " italic" : " font-light"}`}
                          style={{ fontFamily: "var(--font-display)" }}
                        >
                          {phase.subtitle}
                        </p>

                        <p className="text-sm mb-4 leading-relaxed text-[var(--color-text-muted)]">
                          {phase.description}
                        </p>

                        <ul className="space-y-2">
                          {phase.services.map((service, serviceIndex) => (
                            <li
                              key={service}
                              className="text-sm leading-relaxed flex items-start gap-3 text-[var(--color-text-secondary)]"
                            >
                              <span
                                className="text-[9px] font-mono w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                                style={{
                                  color: "var(--color-accent-gold)",
                                  border: "1px solid rgba(201, 162, 39, 0.3)",
                                }}
                              >
                                {String(serviceIndex + 1).padStart(2, "0")}
                              </span>
                              <span>{service}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )
          })}
        </div>

        {/* Section Footer */}
        <div ref={footerRef} className="text-center mt-20 opacity-0">
          <p
            className={`text-xl md:text-2xl mb-8 text-[var(--color-text-secondary)]${locale !== "zh" ? " italic" : " font-light"}`}
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t("method.footer")}
          </p>

          <a
            href="#contact"
            className="inline-block px-8 py-4 border-2 border-[var(--color-accent-gold)] text-[var(--color-accent-gold)] rounded-full text-lg md:text-xl font-medium tracking-wide transition-all duration-300 hover:scale-105 hover:bg-[var(--color-accent-gold)] hover:text-[var(--color-bg-primary)]"
          >
            {t("method.cta")}
          </a>
        </div>
      </div>
    </section>
  )
}
